// src/hooks/useDashboardData.js

import { useCallback, useEffect, useState } from "react";

import { fetchDashboard } from "../../api/api";
import { useDashboardLayouts } from "./useDashboardLayouts";

export const useDashboardData = (token) => {
  const [cards, setCards] = useState([]);
  const [topics, setTopics] = useState([]);
  const [deleted, setDeleted] = useState(false);
  const { layouts, updateLayouts } = useDashboardLayouts(token);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDashboard(token); // Unified endpoint on backend.
      setCards(Array.isArray(data.cards) ? data.cards : []);
      setTopics(data.topics);
      setDeleted(data.deleted);
      updateLayouts("all", data.layouts);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Error fetching dashboard data.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const updateCard = useCallback((updatedCard) => {
    const { card_id, subtype, variant, params } = updatedCard;
    setCards((prev) =>
      prev.map((card) =>
        card.id === card_id
          ? {
              ...card,
              subtype: subtype,
              variant: variant,
              params: params,
            }
          : card
      )
    );
  }, []);

  const createCard = useCallback((newCard) => {
    const transformedCard = {
      ...newCard,
      topics: newCard.topic_ids
        ? newCard.topic_ids.map((id) => ({ id }))
        : newCard.topics || [],
    };

    setCards((prev) => {
      if (prev.some((card) => card.id === transformedCard.id)) return prev;
      return [...prev, transformedCard];
    });
  }, []);

  const cardDeleted = useCallback((deletedCardId) => {
    setCards((prev) => prev.filter((card) => card.id !== deletedCardId));
  }, []);

  useEffect(() => {
    fetchData();
  }, [token]);

  return {
    cards,
    topics,
    deleted,
    layouts,
    error,
    loading,
    updateLayouts,
    updateCard,
    createCard,
    cardDeleted,
  };
};
